<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['send']);
const { t } = useI18n();

const message = ref('');
const textareaRef = ref(null);

const adjustHeight = () => {
  if (!textareaRef.value) return;
  // Reset height to auto to get the correct scrollHeight
  textareaRef.value.style.height = 'auto';
  // Set the height to the scrollHeight
  textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`;
};

// --- Ditado por voz (Web Speech API — transcrição no browser, pt-BR) ---
const SpeechRecognitionImpl =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
const supportsVoice = !!SpeechRecognitionImpl;
const isRecording = ref(false);
let recognition = null;
let textBeforeRecording = '';

const stopRecording = () => {
  if (recognition) {
    // Desanexa o onresult: senão o resultado final (assíncrono) dispara
    // DEPOIS do envio e repopula o box com o texto já enviado.
    recognition.onresult = null;
    recognition.stop();
  }
  isRecording.value = false;
};

const startRecording = () => {
  recognition = new SpeechRecognitionImpl();
  recognition.lang = 'pt-BR';
  recognition.continuous = true;
  recognition.interimResults = true;
  textBeforeRecording = message.value ? `${message.value} ` : '';

  recognition.onresult = event => {
    let transcript = '';
    for (let i = 0; i < event.results.length; i += 1) {
      transcript += event.results[i][0].transcript;
    }
    message.value = textBeforeRecording + transcript;
    nextTick(adjustHeight);
  };
  recognition.onend = () => {
    isRecording.value = false;
  };
  recognition.onerror = () => {
    isRecording.value = false;
  };

  recognition.start();
  isRecording.value = true;
};

const toggleRecording = () => {
  if (!supportsVoice) return;
  if (isRecording.value) stopRecording();
  else startRecording();
};

const sendMessage = () => {
  // Para o ditado por voz ao enviar (senão continua "Ouvindo...").
  if (isRecording.value) stopRecording();
  if (message.value.trim()) {
    emit('send', message.value);
    message.value = '';
    // Reset textarea height after sending
    nextTick(() => {
      adjustHeight();
    });
  }
};

const handleInput = () => {
  nextTick(adjustHeight);
};

const handleEnterKey = event => {
  if (event.isComposing) return;
  event.preventDefault();
  sendMessage();
};

const placeholder = computed(() =>
  isRecording.value
    ? t('CAPTAIN.COPILOT.VOICE_LISTENING')
    : t('CAPTAIN.COPILOT.SEND_MESSAGE')
);

const hasText = computed(() => message.value.trim().length > 0);

onMounted(() => {
  nextTick(adjustHeight);
});
onBeforeUnmount(() => {
  if (recognition) recognition.stop();
});
</script>

<template>
  <form
    class="copilot-input relative flex items-end gap-1 rounded-2xl border bg-n-solid-1 px-1.5 py-1 shadow-sm"
    :class="{ 'copilot-input--recording': isRecording }"
    @submit.prevent="sendMessage"
  >
    <button
      v-if="supportsVoice"
      v-tooltip.top="
        isRecording
          ? t('CAPTAIN.COPILOT.VOICE_STOP')
          : t('CAPTAIN.COPILOT.VOICE_START')
      "
      type="button"
      class="shrink-0 size-9 grid place-items-center rounded-full transition-colors"
      :class="isRecording ? 'copilot-mic--on text-white' : 'copilot-mic'"
      @click="toggleRecording"
    >
      <span
        :class="isRecording ? 'i-ph-square' : 'i-ph-microphone'"
        class="size-[18px]"
      />
    </button>

    <textarea
      ref="textareaRef"
      v-model="message"
      :placeholder="placeholder"
      class="flex-1 reset-base bg-transparent py-2 px-1 text-sm border-0 focus:outline-0 focus:outline-none focus:ring-0 resize-none overflow-hidden max-h-[200px] mb-0 text-n-slate-12 placeholder:text-n-slate-10"
      rows="1"
      @input="handleInput"
      @keydown.enter.exact="handleEnterKey"
    />

    <button
      v-tooltip.top="t('CAPTAIN.COPILOT.ENVIAR')"
      :aria-label="t('CAPTAIN.COPILOT.ENVIAR')"
      type="submit"
      :disabled="!hasText"
      class="copilot-enviar shrink-0 size-9 grid place-items-center rounded-full"
      :class="{ 'copilot-enviar--pronto': hasText }"
    >
      <span class="i-ph-arrow-up size-[18px]" />
    </button>
  </form>
</template>

<style scoped>
/* A MOLDURA. Iris e a cor do Lumos — o campo onde se fala com ele acende na
   cor dele. Rebaixo cinza que sobe para branco no foco, a pele dos campos do
   produto. */
.copilot-input {
  border-color: rgb(var(--slate-5));
  border-radius: 18px;
  background: rgb(var(--slate-2));
  transition:
    border-color 0.18s cubic-bezier(0.16, 1, 0.3, 1),
    box-shadow 0.18s cubic-bezier(0.16, 1, 0.3, 1),
    background-color 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

.copilot-input:focus-within {
  border-color: rgb(var(--iris-8));
  background: rgb(var(--solid-1));
  box-shadow: 0 0 0 4px rgb(var(--iris-9) / 0.14);
}

/* O estilo global dá a TODO textarea um contorno de 1px (medido: outline
   rgb(234 234 234) solid 1px) — era a caixa dentro da caixa do campo. */
.copilot-input textarea {
  outline: none;
}

.copilot-mic {
  color: rgb(var(--slate-11));
  transition:
    background 0.14s ease,
    color 0.14s ease;
}

.copilot-mic:hover {
  background: rgb(var(--iris-3));
  color: rgb(var(--iris-11));
}

.dark .copilot-mic:hover {
  background: rgb(var(--iris-4) / 0.4);
}

/* Gravando: rubi, que no produto e a cor do que e irreversivel/em curso — era
   `#ef4444`, um vermelho fora da escala, repetido em tres lugares. */
.copilot-mic--on {
  background-color: rgb(var(--ruby-9));
  animation: copilot-pulse 1.4s ease-in-out infinite;
}

.copilot-input--recording {
  border-color: rgb(var(--ruby-9)) !important;
}

@keyframes copilot-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgb(var(--ruby-9) / 0.45);
  }

  50% {
    box-shadow: 0 0 0 6px rgb(var(--ruby-9) / 0);
  }
}

/* O BOTAO DE ENVIAR ACORDA — mesma ideia do compositor do chat interno: ele
   nasce neutro e vira iris no instante em que ha texto. */
.copilot-enviar {
  background: rgb(var(--slate-4));
  color: rgb(var(--slate-9));
  transition:
    background 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
}

.copilot-enviar--pronto {
  background: linear-gradient(140deg, rgb(var(--iris-9)), rgb(var(--violet-9)));
  color: #fff;
  box-shadow: 0 2px 8px rgb(var(--iris-9) / 0.35);
  animation: copilotEnviarAcorda 0.32s cubic-bezier(0.34, 1.12, 0.44, 1);
}

.copilot-enviar--pronto:hover {
  box-shadow: 0 4px 12px rgb(var(--iris-9) / 0.45);
}

@keyframes copilotEnviarAcorda {
  from {
    transform: scale(0.86);
  }

  60% {
    transform: scale(1.08);
  }
}

@media (prefers-reduced-motion: reduce) {
  .copilot-mic--on,
  .copilot-enviar--pronto {
    animation: none;
  }
}
</style>
